import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiGet, IS_MOCK } from "../../lib/api";
import type {
  MovimentoExtrato, SaldoConta, BancoConta, ContaDetalhe,
  ParContasDuplicadas, ComparacaoContas,
} from "./types";

export const extratoKey = (empresa?: string, periodo?: string, banco?: string, conta?: string) =>
  ["extrato-completo", empresa ?? "", periodo ?? "", banco ?? "", conta ?? ""] as const;
export const saldoKey = (empresa?: string, banco?: string) => ["saldo-conta", empresa ?? "", banco ?? ""] as const;
export const bancosKey = (empresa?: string, periodo?: string) => ["bancos-disponiveis", empresa ?? "", periodo ?? ""] as const;
export const periodosExtratoKey = (empresa?: string) => ["periodos-extrato", empresa ?? ""] as const;

export function usePeriodosExtrato(empresa?: string) {
  return useQuery<string[]>({
    queryKey: periodosExtratoKey(empresa),
    queryFn: () => apiGet<string[]>(`/api/extratos/periodos?empresa=${empresa}`),
    enabled: !!empresa,
  });
}

// Todos os movimentos da empresa (não só os órfãos) — a aba "Extrato Bancário"
// mostra o extrato tal como o banco o apresenta, com o estado de cada linha.
export function useExtratoCompleto(empresa?: string, periodo?: string, banco?: string, conta?: string) {
  return useQuery<MovimentoExtrato[]>({
    queryKey: extratoKey(empresa, periodo, banco, conta),
    queryFn: () =>
      apiGet<MovimentoExtrato[]>(
        `/api/conciliacao/extrato?empresa=${empresa}${periodo ? `&periodo=${periodo}` : ""}${banco ? `&banco=${encodeURIComponent(banco)}` : ""}${conta ? `&conta=${encodeURIComponent(conta)}` : ""}`
      ),
    enabled: !!empresa && !!periodo,
  });
}

// Combinações banco+conta com movimentos importados para esta empresa —
// alimenta o filtro (uma empresa pode ter mais do que uma conta no mesmo banco).
export function useBancosDisponiveis(empresa?: string, periodo?: string) {
  return useQuery<BancoConta[]>({
    queryKey: bancosKey(empresa, periodo),
    queryFn: () => apiGet<BancoConta[]>(`/api/extratos/bancos?empresa=${empresa}${periodo ? `&periodo=${periodo}` : ""}`),
    enabled: !!empresa && !!periodo,
  });
}

// Sem banco: devolve a lista de todas as contas conhecidas da empresa. Com
// banco: devolve só essa conta.
export function useSaldoConta(empresa?: string, banco?: string) {
  return useQuery<SaldoConta | SaldoConta[] | null>({
    queryKey: saldoKey(empresa, banco),
    queryFn: () =>
      apiGet<SaldoConta | SaldoConta[] | null>(
        `/api/extratos/saldo?empresa=${empresa}${banco ? `&banco=${encodeURIComponent(banco)}` : ""}`
      ),
    enabled: !!empresa,
  });
}

// Confirma/rejeita uma sugestão de match a partir da aba Extrato Bancário —
// invalida a cache do extrato completo E a lista de faturas pendentes (esta
// mutação muda "pago" da fatura tanto a confirmar como a rejeitar, tal como
// useCorrigirMatch já fazia -- faltava aqui, deixando a aba Faturas
// Pendentes com uma fatura já paga/já não-paga até um refresh manual).
export function useConfirmMatchExtrato(empresa?: string, periodo?: string, banco?: string, conta?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ matchId, estado }: { matchId: number; estado: "confirmado" | "rejeitado" }) => {
      if (IS_MOCK) return;
      const r = await apiFetch(`/api/reconciliacao/matches/${matchId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    },
    onSuccess: () => {
      if (IS_MOCK) return;
      qc.invalidateQueries({ queryKey: extratoKey(empresa, periodo, banco, conta) });
      qc.invalidateQueries({ queryKey: ["faturas-pendentes", empresa ?? ""] });
    },
  });
}

// Corrige a associação de um movimento: rejeita o match antigo (se existir —
// pendente ou já confirmado) e associa uma ou mais faturas ao seu lugar
// ("outro conjunto de pagamentos" — um único movimento pode cobrir várias
// faturas). Os dois passos correm em sequência; se o reject falhar, não
// tenta criar os novos matches.
//
// Quando é mais do que uma fatura, passa pela mesma pipeline de batch de
// pagamentos já usada no Dashboard (/api/reconciliacao/matches/lote →
// _criar_batch_pagamento + _confirmar_pagamento_batch no backend) em vez de
// criar vários matches manuais soltos — o contabilista confirmou que é assim
// que uma linha de extrato pode cobrir várias faturas.
export function useCorrigirMatch(empresa?: string, periodo?: string, banco?: string, conta?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      extratoId, faturaIds, matchIdAntigo,
    }: { extratoId: number; faturaIds: number[]; matchIdAntigo?: number | null }) => {
      if (IS_MOCK) return;
      if (matchIdAntigo != null) {
        const r = await apiFetch(`/api/reconciliacao/matches/${matchIdAntigo}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estado: "rejeitado" }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      }
      if (faturaIds.length > 1) {
        const r = await apiFetch(`/api/reconciliacao/matches/lote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ extrato_id: extratoId, fatura_ids: faturaIds }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(typeof d?.detail === "string" ? d.detail : `HTTP ${r.status}`);
        }
        return;
      }
      for (const faturaId of faturaIds) {
        const r = await apiFetch(`/api/reconciliacao/matches/manual?fatura_id=${faturaId}&extrato_id=${extratoId}`, {
          method: "POST",
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(typeof d?.detail === "string" ? d.detail : `HTTP ${r.status}`);
        }
      }
    },
    // onSettled (não só onSuccess) — se o lote for parcial (ex: a 2ª de 3
    // faturas falhar a meio), a 1ª já ficou associada no servidor; invalidar
    // sempre garante que a tabela reflete o que realmente ficou gravado.
    onSettled: () => {
      if (IS_MOCK) return;
      qc.invalidateQueries({ queryKey: extratoKey(empresa, periodo, banco, conta) });
      qc.invalidateQueries({ queryKey: ["faturas-pendentes", empresa ?? ""] });
    },
  });
}

export const duplicadosKey = (empresa?: string) => ["contas-duplicadas", empresa ?? ""] as const;
export const comparacaoKey = (empresa?: string, a?: number, b?: number) =>
  ["contas-comparacao", empresa ?? "", a ?? 0, b ?? 0] as const;

// Pares de contas desta empresa que parecem ser a MESMA conta física — marca
// os cartões com o aviso de possível duplicado.
export function useContasDuplicadas(empresa?: string) {
  return useQuery<ParContasDuplicadas[]>({
    queryKey: duplicadosKey(empresa),
    queryFn: () => apiGet<ParContasDuplicadas[]>(`/api/contas-duplicados?empresa=${empresa}`),
    enabled: !!empresa,
  });
}

// Comparação lado a lado de duas contas. Só corre quando o painel está
// aberto (`enabled`) — é uma leitura pesada em relação ao resto do ecrã.
export function useCompararContas(empresa?: string, a?: number, b?: number) {
  return useQuery<ComparacaoContas>({
    queryKey: comparacaoKey(empresa, a, b),
    queryFn: () => apiGet<ComparacaoContas>(`/api/contas/${a}/comparar/${b}?empresa=${empresa}`),
    enabled: !!empresa && !!a && !!b,
  });
}

// Edita banco e número de UMA conta, por conta_id.
//
// Substitui useCorrigirContaSaldo (endereçado por banco), que só conseguia
// completar contas ainda SEM número — corrigir um número já preenchido caía
// no INSERT e criava uma segunda conta. Ver PATCH /api/contas/{conta_id}.
//
// Invalida tudo o que depende da identidade da conta: os cartões, os
// movimentos (o filtro do extrato é por banco+conta, muda quando o número
// muda), os saldos e a lista de duplicados.
export function useEditarConta(empresa?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ contaId, banco, conta }: { contaId: number; banco?: string; conta?: string }) => {
      if (IS_MOCK) return null as unknown as ContaDetalhe;
      const r = await apiFetch(`/api/contas/${contaId}?empresa=${empresa}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banco, conta }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        const detalhe = typeof d?.detail === "string" ? d.detail : d?.detail?.msg;
        const erro = new Error(detalhe || `HTTP ${r.status}`);
        (erro as Error & { status?: number; detail?: unknown }).status = r.status;
        (erro as Error & { status?: number; detail?: unknown }).detail = d?.detail;
        throw erro;
      }
      return (await r.json()) as ContaDetalhe;
    },
    onSuccess: () => {
      if (IS_MOCK) return;
      qc.invalidateQueries({ queryKey: ["bancos-disponiveis", empresa ?? ""] });
      qc.invalidateQueries({ queryKey: ["extrato-completo", empresa ?? ""] });
      qc.invalidateQueries({ queryKey: ["saldo-conta", empresa ?? ""] });
      qc.invalidateQueries({ queryKey: duplicadosKey(empresa) });
    },
  });
}

// Fusão de duas contas duplicadas.
//
// O MESMO endpoint serve o dry-run e a execução — `confirmar` é o que os
// separa, e omitir esse campo nunca escreve nada. Assim o plano que a pessoa
// vê e o que é executado saem exatamente do mesmo código; um dry-run à parte
// podia divergir da execução real sem ninguém dar por isso.
export function useFundirContas(empresa?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      origem, destino, confirmar = false, removerDuplicados = false,
    }: { origem: number; destino: number; confirmar?: boolean; removerDuplicados?: boolean }) => {
      if (IS_MOCK) return null;
      const r = await apiFetch(`/api/contas/fundir?empresa=${empresa}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conta_id_origem: origem,
          conta_id_destino: destino,
          confirmar,
          remover_duplicados: removerDuplicados,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detalhe = typeof d?.detail === "string" ? d.detail : d?.detail?.msg;
        throw new Error(detalhe || `HTTP ${r.status}`);
      }
      return d;
    },
    onSuccess: (d) => {
      // Um dry-run não muda nada — invalidar aí só provocava recargas inúteis.
      if (IS_MOCK || !d || (d as { dry_run?: boolean }).dry_run) return;
      qc.invalidateQueries({ queryKey: ["bancos-disponiveis", empresa ?? ""] });
      qc.invalidateQueries({ queryKey: ["extrato-completo", empresa ?? ""] });
      qc.invalidateQueries({ queryKey: ["saldo-conta", empresa ?? ""] });
      qc.invalidateQueries({ queryKey: duplicadosKey(empresa) });
      qc.invalidateQueries({ queryKey: ["contas-comparacao", empresa ?? ""] });
    },
  });
}

// Grava a ordem manual dos movimentos de um MESMO dia (arrasto na tabela) —
// o banco não garante ordem nenhuma entre movimentos do mesmo dia. O backend
// exige o dia inteiro de uma vez (ver POST /api/extratos/reordenar), por isso
// quem chama tem de enviar todos os ids desse dia, não só os que se moveram.
export function useReordenarExtrato(empresa?: string, periodo?: string, banco?: string, conta?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids }: { ids: number[] }) => {
      if (IS_MOCK) return;
      const r = await apiFetch("/api/extratos/reordenar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d?.detail === "string" ? d.detail : `HTTP ${r.status}`);
      }
    },
    onSuccess: () => {
      if (!IS_MOCK) qc.invalidateQueries({ queryKey: extratoKey(empresa, periodo, banco, conta) });
    },
  });
}

// Marca/desmarca um movimento como "resolvido sem fatura" — para os casos que
// nunca vão ter fatura correspondente (comissões, TSU, entre contas, etc.).
// Ao contrário de apagar, o movimento continua visível no extrato.
export function useMarcarResolvido(empresa?: string, periodo?: string, banco?: string, conta?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, nota, resolver }: { id: number; nota?: string; resolver: boolean }) => {
      if (IS_MOCK) return;
      const path = resolver
        ? `/api/extratos/${id}/marcar-resolvido`
        : `/api/extratos/${id}/reativar-resolvido`;
      const r = await apiFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: resolver ? JSON.stringify({ nota: nota || null }) : undefined,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    },
    onSuccess: () => {
      if (!IS_MOCK) qc.invalidateQueries({ queryKey: extratoKey(empresa, periodo, banco, conta) });
    },
  });
}
